import React, { useEffect, useState } from "react";
import { Box, Button, TextField, MenuItem, Typography } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Header from "../../components/Header";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";

const CourseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const isEdit = Boolean(id);
  const isProfessional = location.pathname.includes("professional-skills");

  const [formValues, setFormValues] = useState({
    categoryId: 1,
    title: "",
    shortDescription: "",
    learningObjectives: "",
    body: "",
    videoUrl: "",
    duration: 0,
    price: 0,
    offerPrice: 0,
    type: isProfessional ? 1 : 2,
    imageFile: "",
  });

  const [categories, setCategories] = useState([]);

  // Fetch categories
  useEffect(() => {
    axiosInstance
      .get("/courses/categories")
      .then((res) => setCategories(res.data))
      .catch((err) => console.error(err));
  }, []);

  // Fetch existing course data if editing
  useEffect(() => {
    if (!isEdit) return;

    axiosInstance
      .get(`/courses/${id}`)
      .then((res) => {
        const data = res.data;
        setFormValues({
          categoryId: data.categoryId || 1,
          title: data.title || "",
          shortDescription: data.shortDescription || "",
          learningObjectives: data.learningObjectives || "",
          body: data.body || "",
          videoUrl: data.videoUrl || "",
          duration: data.duration || 0,
          price: data.price || 0,
          offerPrice: data.offerPrice || 0,
          type: data.typeId || 1,
          imageFile: data.imageFile || null,
          imagePath: data.imagePath || "", // URL for existing image
        });
      })
      .catch((err) => console.error(err));
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "imageFile" && files && files[0]) {
      // Store the uploaded file object
      setFormValues((prev) => ({ ...prev, imageFile: files[0] }));
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("categoryId", formValues.categoryId);
    formData.append("title", formValues.title);
    formData.append("shortDescription", formValues.shortDescription);
    formData.append("learningObjectives", formValues.learningObjectives);
    formData.append("body", formValues.body);
    formData.append("videoUrl", formValues.videoUrl);
    formData.append("duration", formValues.duration);
    formData.append("price", formValues.price);
    formData.append("offerPrice", formValues.offerPrice);
    formData.append("type", formValues.type);

    if (formValues.imageFile) {
      formData.append("imageFile", formValues.imageFile); // only if user selected a file
    }

    try {
      const url = isEdit ? `/courses/${id}` : "/courses";
      const method = isEdit ? "put" : "post";

      await axiosInstance[method](url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(
        isEdit ? "Course updated successfully" : "Course added successfully",
      );
      navigate(isProfessional ? "/professional-skills" : "/soft-skills");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error submitting course");
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEdit ? "Edit Course" : "Add New Course"}
        subtitle={formValues.type === 1 ? "Professional Skills" : "Soft Skills"}
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        display="flex"
        flexDirection="column"
        gap={2}
        maxWidth="600px"
      >
        <TextField
          select
          label="Category"
          name="categoryId"
          value={formValues.categoryId}
          onChange={handleChange}
          fullWidth
        >
          {categories.map((cat) => (
            <MenuItem key={cat.id} value={cat.id}>
              {cat.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Type"
          name="type"
          value={formValues.type}
          onChange={handleChange}
          fullWidth
        >
          <MenuItem value={1}>Professional</MenuItem>
          <MenuItem value={2}>Soft</MenuItem>
        </TextField>

        <TextField
          label="Title"
          name="title"
          value={formValues.title}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Short Description"
          name="shortDescription"
          value={formValues.shortDescription}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Learning Objectives"
          name="learningObjectives"
          value={formValues.learningObjectives}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Body"
          name="body"
          value={formValues.body}
          onChange={handleChange}
          multiline
          rows={4}
          fullWidth
        />
        <TextField
          label="Video URL"
          name="videoUrl"
          value={formValues.videoUrl}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Duration (hours)"
          name="duration"
          type="number"
          value={formValues.duration}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Price"
          name="price"
          type="number"
          value={formValues.price}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Offer Price"
          name="offerPrice"
          type="number"
          value={formValues.offerPrice}
          onChange={handleChange}
          fullWidth
        />

        {/* Show existing image if available */}
        {formValues.imagePath && (
          <Box mb={2}>
            <Typography>Current Image:</Typography>
            <img
              src={formValues.imagePath} // URL from server
              alt="Course"
              style={{ maxWidth: "200px", marginBottom: "10px" }}
            />
          </Box>
        )}

        {/* Upload new image */}
        <Button variant="contained" component="label">
          Upload New Image
          <input
            type="file"
            hidden
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) setFormValues((prev) => ({ ...prev, imageFile: file }));
            }}
          />
        </Button>

        <Button variant="contained" color="secondary" type="submit">
          {isEdit ? "Update Course" : "Add Course"}
        </Button>
      </Box>
    </Box>
  );
};

export default CourseForm;
